import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAdminCustomers,
  createAccessGrant,
  adminMarkPaid,
  adminComp,
  adminLock,
  adminCheckoutLink,
  adminExempt,
  adminDeleteCompany,
  type AdminCustomerRow,
} from '../../api/billing'
import { useActionToast } from '../../lib/useActionToast'
import AdminExemptDialog from './AdminExemptDialog'
import AdminCompanyDeleteDialog from './AdminCompanyDeleteDialog'
import '../../components/billing/subscriptionBilling.css'

type StatusFilter = 'all' | 'paying' | 'comped' | 'grace' | 'exempt' | 'locked' | 'none'

const STATUS_CHIPS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'paying', label: 'Paying' },
  { key: 'comped', label: 'Comped' },
  { key: 'grace', label: 'Grace' },
  { key: 'exempt', label: 'Exempt' },
  { key: 'locked', label: 'Locked' },
  { key: 'none', label: 'None' },
]

const GRANT_LABELS: Record<'checkout_required' | 'comped' | 'exempt', string> = {
  checkout_required: 'Pay via Stripe',
  comped: 'Free (comped)',
  exempt: 'Never billed (exempt)',
}

function matchesSearch(row: AdminCustomerRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const company = (row.company_name || '').toLowerCase()
  const owner = (row.owner_email || '').toLowerCase()
  return company.includes(q) || owner.includes(q)
}

function matchesStatus(row: AdminCustomerRow, filter: StatusFilter): boolean {
  const status = row.subscription_status || 'none'
  if (filter === 'all') return true
  if (filter === 'paying') return ['active', 'trialing', 'past_due'].includes(status)
  if (filter === 'locked') return status === 'locked'
  return status === filter
}

export default function AdminCustomersScreen() {
  const qc = useQueryClient()
  const { showToast, Toast } = useActionToast()
  const [grantEmail, setGrantEmail] = useState('')
  const [grantType, setGrantType] = useState<'comped' | 'checkout_required' | 'exempt'>('checkout_required')
  const [grantNote, setGrantNote] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [exemptRow, setExemptRow] = useState<AdminCustomerRow | null>(null)
  const [deleteRow, setDeleteRow] = useState<AdminCustomerRow | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-customers'],
    queryFn: fetchAdminCustomers,
    staleTime: 30_000,
  })

  const tiles = data?.tiles
  const rows = data?.rows ?? []

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesSearch(row, search) && matchesStatus(row, statusFilter)),
    [rows, search, statusFilter],
  )

  async function runAction(companyId: number, fn: () => Promise<void>) {
    setBusyId(companyId)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['admin-customers'] })
      showToast('Updated')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setBusyId(null)
    }
  }

  async function submitGrant(e: React.FormEvent) {
    e.preventDefault()
    try {
      await createAccessGrant({ email: grantEmail, grant_type: grantType, note: grantNote || undefined })
      setGrantEmail('')
      setGrantNote('')
      showToast('Pre-approval saved')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed')
    }
  }

  async function confirmExempt(note: string) {
    if (!exemptRow) return
    await runAction(exemptRow.id, async () => {
      await adminExempt(exemptRow.id, note)
      setExemptRow(null)
    })
  }

  async function confirmDelete(confirmName: string) {
    if (!deleteRow) return
    setBusyId(deleteRow.id)
    try {
      await adminDeleteCompany(deleteRow.id, confirmName)
      await qc.invalidateQueries({ queryKey: ['admin-customers'] })
      setDeleteRow(null)
      showToast('Company deleted')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="admin-customers-page" data-testid="admin-customers-page">
      <h1 className="page-title">Customers</h1>
      <p className="page-lead">Super admin billing controls · v1.1</p>

      {tiles && tiles.price_configured === false && (
        <div className="admin-customers-price-alert" data-testid="admin-price-not-configured" role="alert">
          Subscription price not configured
        </div>
      )}

      {tiles && (
        <div className="admin-customers-tiles" data-testid="admin-customers-tiles">
          <div className="kpi-card"><span>Paying</span><strong>{tiles.paying}</strong></div>
          <div className="kpi-card"><span>Comped</span><strong>{tiles.comped}</strong></div>
          <div className="kpi-card"><span>Grace</span><strong>{tiles.grace}</strong></div>
          <div className="kpi-card"><span>Locked / none</span><strong>{tiles.locked}</strong></div>
          <div className="kpi-card"><span>MRR</span><strong>${tiles.mrr ?? 0}</strong></div>
          <div className="kpi-card"><span>ARR</span><strong>${tiles.arr ?? 0}</strong></div>
          <div className="kpi-card"><span>Failed</span><strong>{tiles.failed ?? 0}</strong></div>
          <div className="kpi-card"><span>Renewals 30d</span><strong>{tiles.renewals30 ?? 0}</strong></div>
        </div>
      )}

      <form className="admin-grant-form" onSubmit={(e) => void submitGrant(e)} data-testid="admin-grant-form">
        <h2>Pre-approve email</h2>
        <input
          type="email"
          placeholder="owner@company.com"
          value={grantEmail}
          onChange={(e) => setGrantEmail(e.target.value)}
          data-testid="admin-grant-email"
          required
        />
        <select value={grantType} onChange={(e) => setGrantType(e.target.value as typeof grantType)} data-testid="admin-grant-type">
          <option value="checkout_required">{GRANT_LABELS.checkout_required}</option>
          <option value="comped">{GRANT_LABELS.comped}</option>
          <option value="exempt">{GRANT_LABELS.exempt}</option>
        </select>
        <input type="text" placeholder="Note" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} />
        <button type="submit" className="btn primary">Save grant</button>
      </form>

      <div className="admin-customers-toolbar" data-testid="admin-customers-toolbar">
        <input
          className="hub-search"
          data-testid="admin-customers-search"
          placeholder="Search company or owner email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="hub-chips" data-testid="admin-customers-status-chips">
          {STATUS_CHIPS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`hub-chip${statusFilter === key ? ' on' : ''}`}
              data-testid={`admin-status-chip-${key}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}{key === 'all' ? ` · ${rows.length}` : ''}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p>Loading…</p>}
      <table className="admin-customers-table" data-testid="admin-customers-table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Owner</th>
            <th>Status</th>
            <th>Next charge</th>
            <th>Note</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row) => (
            <tr key={row.id} data-testid={`admin-customer-row-${row.id}`}>
              <td>{row.company_name || `Company ${row.id}`}</td>
              <td>{row.owner_email || '—'}</td>
              <td data-testid={`admin-customer-status-${row.id}`}>{row.subscription_status || '—'}</td>
              <td>{row.current_period_end ? new Date(row.current_period_end).toLocaleDateString() : '—'}</td>
              <td>{row.billing_note || '—'}</td>
              <td className="admin-customers-actions">
                <button type="button" className="btn b-ol btn-sm" disabled={busyId === row.id}
                  onClick={() => setExemptRow(row)} data-testid={`admin-exempt-btn-${row.id}`}>Exempt</button>
                <button type="button" className="btn b-ol btn-sm" disabled={busyId === row.id}
                  onClick={() => void runAction(row.id, () => adminMarkPaid(row.id))}>Mark paid</button>
                <button type="button" className="btn b-ol btn-sm" disabled={busyId === row.id}
                  onClick={() => void runAction(row.id, () => adminComp(row.id, new Date(Date.now() + 30 * 86400000).toISOString()))}>Comp</button>
                <button type="button" className="btn b-ol btn-sm" disabled={busyId === row.id}
                  onClick={() => void runAction(row.id, () => adminLock(row.id))}>Lock</button>
                <button type="button" className="btn b-ol btn-sm" disabled={busyId === row.id}
                  data-testid={`admin-checkout-link-${row.id}`}
                  onClick={async () => {
                    try {
                      const url = await adminCheckoutLink(row.id)
                      await navigator.clipboard.writeText(url)
                      showToast('Checkout link copied')
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : 'Copy failed')
                    }
                  }}>Copy checkout link</button>
                <button
                  type="button"
                  className="btn b-ol btn-sm danger"
                  disabled={busyId === row.id || !row.can_delete}
                  title={row.can_delete ? 'Delete company and its users' : (row.delete_block_reason || 'Cannot delete')}
                  data-testid={`admin-delete-btn-${row.id}`}
                  onClick={() => row.can_delete && setDeleteRow(row)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <AdminExemptDialog
        companyName={exemptRow?.company_name || ''}
        open={!!exemptRow}
        busy={busyId === exemptRow?.id}
        onClose={() => setExemptRow(null)}
        onConfirm={(note) => void confirmExempt(note)}
      />
      <AdminCompanyDeleteDialog
        companyName={deleteRow?.company_name || ''}
        open={!!deleteRow}
        busy={busyId === deleteRow?.id}
        onClose={() => setDeleteRow(null)}
        onConfirm={(confirmName) => void confirmDelete(confirmName)}
      />
      {Toast}
    </div>
  )
}
